/**
 * VERA Audit Service
 *
 * Client-side service that tracks login sessions by calling the
 * /api/audit-* Azure Functions endpoints. Captures:
 *   - Source IP (server-side from x-forwarded-for)
 *   - Login time & date
 *   - Session duration (via heartbeat every 5 min + logout/unload event)
 *   - Username and user-agent
 *
 * Lifecycle:
 *   1. onLogin()   — POST /api/audit-login → starts session
 *   2. heartbeat   — POST /api/audit-heartbeat every 5 min (auto)
 *   3. onLogout()  — POST /api/audit-heartbeat {action:"logout"}
 *   4. page unload — navigator.sendBeacon for reliable logout
 */

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const SESSION_AUDIT_KEY = "vera_audit_session";

interface AuditSession {
  sessionId: string;
  partitionKey: string;
  loginTime: string;
  username: string;
}

function generateSessionId(): string {
  // Compact unique ID: timestamp + random hex
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 10);
  return `${ts}-${rand}`;
}

function getStoredSession(): AuditSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_AUDIT_KEY);
    if (raw) return JSON.parse(raw) as AuditSession;
  } catch {
    // ignore
  }
  return null;
}

function storeSession(session: AuditSession): void {
  sessionStorage.setItem(SESSION_AUDIT_KEY, JSON.stringify(session));
}

function clearSession(): void {
  sessionStorage.removeItem(SESSION_AUDIT_KEY);
}

let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

/** Call the API, swallowing errors so audit never disrupts the app. */
async function callApi(path: string, body: Record<string, unknown>): Promise<Record<string, unknown> | null> {
  try {
    const resp = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (resp.ok) return await resp.json();
    console.warn(`[Audit] ${path} returned ${resp.status}`);
  } catch (err) {
    console.warn("[Audit] API call failed:", err);
  }
  return null;
}

/** Send heartbeat for active session. */
async function sendHeartbeat(): Promise<void> {
  const session = getStoredSession();
  if (!session) return;

  await callApi("/api/audit-heartbeat", {
    sessionId: session.sessionId,
    partitionKey: session.partitionKey,
    action: "heartbeat",
  });
}

/** Handle page unload — send logout via beacon for reliability. */
function handleUnload(): void {
  const session = getStoredSession();
  if (!session) return;

  const payload = JSON.stringify({
    sessionId: session.sessionId,
    partitionKey: session.partitionKey,
    action: "logout",
  });

  // navigator.sendBeacon is reliable during page unload
  if (navigator.sendBeacon) {
    navigator.sendBeacon(
      "/api/audit-heartbeat",
      new Blob([payload], { type: "application/json" })
    );
  }
}

// ── Public API ────────────────────────────────────────────────

/**
 * Called after successful login. Records the event server-side and
 * starts the heartbeat timer.
 */
export async function onLogin(username: string): Promise<void> {
  const sessionId = generateSessionId();

  const result = await callApi("/api/audit-login", { sessionId, username });

  const session: AuditSession = {
    sessionId,
    partitionKey: result?.partitionKey as string || formatPartitionKey(new Date()),
    loginTime: (result?.loginTime as string) || new Date().toISOString(),
    username,
  };
  storeSession(session);

  // Start heartbeat
  stopHeartbeat();
  heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

  // Register unload handler
  window.addEventListener("beforeunload", handleUnload);
}

/**
 * Called on explicit logout. Sends a logout event and stops tracking.
 */
export async function onLogout(): Promise<void> {
  const session = getStoredSession();
  if (session) {
    await callApi("/api/audit-heartbeat", {
      sessionId: session.sessionId,
      partitionKey: session.partitionKey,
      action: "logout",
    });
  }

  stopHeartbeat();
  window.removeEventListener("beforeunload", handleUnload);
  clearSession();
}

/**
 * Resume heartbeat for an existing session (e.g. after page refresh
 * where sessionStorage still has the auth token).
 */
export function resumeSession(): void {
  const session = getStoredSession();
  if (!session) return;

  stopHeartbeat();
  heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
  window.addEventListener("beforeunload", handleUnload);

  // Send an immediate heartbeat to update "lastSeen"
  sendHeartbeat();
}

/** Stop the heartbeat timer. */
function stopHeartbeat(): void {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/** Format YYYY-MM partition key. */
function formatPartitionKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// ── Query API (for admin page) ────────────────────────────────

export interface AuditRecord {
  sessionId: string;
  username: string;
  clientIp: string;
  userAgent: string;
  loginTime: string;
  lastSeen: string;
  logoutTime: string | null;
  durationSeconds: number;
  status: string;
}

export async function fetchAuditLog(month?: string, limit = 100): Promise<AuditRecord[]> {
  try {
    const params = new URLSearchParams();
    if (month) params.set("month", month);
    params.set("limit", String(limit));

    const resp = await fetch(`/api/audit-query?${params.toString()}`);
    if (!resp.ok) return [];

    const data = await resp.json();
    return data.records || [];
  } catch {
    console.warn("[Audit] Failed to fetch audit log");
    return [];
  }
}
